import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { MovementHistoryPoint } from '../../../types/inventory';

interface MovementHistoryChartProps {
    data: MovementHistoryPoint[];
    isLoading?: boolean;
}

function formatNumber(value: number): string {
    return new Intl.NumberFormat('de-CH', { maximumFractionDigits: 0 }).format(value);
}

function formatCompact(value: number): string {
    if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
    return value.toFixed(0);
}

function formatMonthShort(monthKey: string): string {
    const [y, m] = monthKey.split('-');
    const date = new Date(Number(y), Number(m) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

const SERIES_META: Array<{ key: 'purchase' | 'sale' | 'adjustments'; label: string; color: string }> = [
    { key: 'purchase', label: 'Purchase', color: '#10b981' },
    { key: 'sale', label: 'Sale', color: '#ef4444' },
    { key: 'adjustments', label: 'Adjustments', color: '#94a3b8' },
];

interface MovementTooltipProps {
    active?: boolean;
    payload?: Array<{ payload: MovementHistoryPoint }>;
}

function MovementTooltip({ active, payload }: MovementTooltipProps) {
    if (!active || !payload || payload.length === 0) return null;
    const point = payload[0].payload;
    const customers = point.salesByCustomer ?? [];

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-2.5 text-xs shadow-lg">
            <div className="mb-1.5 font-semibold text-gray-900">{formatMonthShort(point.month)}</div>
            <div className="space-y-0.5">
                {SERIES_META.map(s => (
                    <div key={s.key} className="flex items-center justify-between gap-4">
                        <span className="flex items-center gap-1.5 text-gray-500">
                            <span className="inline-block h-2 w-2 rounded-sm" style={{ backgroundColor: s.color }} />
                            {s.label}
                        </span>
                        <span className="font-mono text-gray-900">{formatNumber(point[s.key])}</span>
                    </div>
                ))}
            </div>
            {customers.length > 0 && (
                <div className="mt-2 border-t border-gray-100 pt-1.5">
                    <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">Sales by Customer</div>
                    <div className="space-y-0.5">
                        {customers.map((c, i) => (
                            <div key={i} className="flex items-center justify-between gap-4">
                                <span className="max-w-[180px] truncate text-gray-600">{c.name}</span>
                                <span className="font-mono text-gray-900">{formatNumber(c.qty)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export function MovementHistoryChart({ data, isLoading }: MovementHistoryChartProps) {
    if (isLoading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            </div>
        );
    }

    if (!data || data.length === 0) {
        return <div className="py-8 text-center text-sm text-gray-400">No movement history available.</div>;
    }

    return (
        <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                    dataKey="month"
                    tickFormatter={formatMonthShort}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    interval="preserveStartEnd"
                    minTickGap={24}
                />
                <YAxis
                    tickFormatter={formatCompact}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    width={55}
                />
                <Tooltip content={<MovementTooltip />} cursor={{ fill: '#f1f5f9' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="purchase" name="Purchase" fill="#10b981" radius={[2, 2, 0, 0]} />
                <Bar dataKey="sale" name="Sale" fill="#ef4444" radius={[2, 2, 0, 0]} />
                <Bar dataKey="adjustments" name="Adjustments" fill="#94a3b8" radius={[2, 2, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
    );
}
